import React, { useMemo } from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Button } from '@sololearnorg/sol';

import { useLocation, useSearchParams } from 'react-router-dom';
import { PaginationCTAType, PaginationItem } from './types';
import s from './SlPagination.scss';
import { LocalizedLink } from '../LocalizedLink/LocalizedLink';
import { i18n } from '../../../symphony';

interface IPaginationProps {
    totalCount: number;
    rowsPerPage: number;
	onItemClick: ({
		paginationCtaType,
		currentPageNumber,
		clickedPageNumber,
	}: PaginationItem) => void;
}

export function SlPagination({
	totalCount,
	rowsPerPage,
	onItemClick,
}: IPaginationProps): JSX.Element {
	useStyles(s);
	const { search, pathname } = useLocation();
	const [searchParams] = useSearchParams();
	const currentPageNumber = +searchParams.get('page') || 1;
	const pagesCount = Math.ceil(totalCount / rowsPerPage);

	const generateUrl = (pageNumber: number) => {
		const url = new URLSearchParams(search);

		url.set('page', pageNumber.toString());

		return `${pathname}?${url.toString()}`;
	};

	const pages = useMemo(
		() => [
			{
				pageNumber: 1,
				key: '1',
			},
			...(currentPageNumber > 3
				? [{ pageNumber: '...', isDisabled: true, key: '2' }]
				: []),
			...(currentPageNumber > 2
				? [{ pageNumber: currentPageNumber - 1, key: '3' }]
				: []),
			...(currentPageNumber > 1 && currentPageNumber < pagesCount
				? [
					{
						pageNumber: currentPageNumber,
						key: '4',
					},
				]
				: []),
			...(currentPageNumber < pagesCount - 1
				? [
					{
						pageNumber: currentPageNumber + 1,
						key: '5',
					},
				]
				: []),
			...(currentPageNumber < pagesCount - 2
				? [{ pageNumber: '...', isDisabled: true, key: '6' }]
				: []),
			{
				pageNumber: pagesCount,
				key: '7',
			},
		],
		[currentPageNumber, pagesCount],
	);

	const getButtonType = (
		activePageNumber: number,
		buttonPageNumber: number,
		isDisabled: boolean,
	) => {
		if (isDisabled) return 'tertiary';

		if (buttonPageNumber === activePageNumber) return 'primary';

		return 'secondary';
	};

	if (pagesCount < 2) return null;

	const handleItemClick = (
		paginationCtaType: PaginationCTAType,
		pageNumber?: number,
	) => {
		let clickedPageNumber = pageNumber;

		if (paginationCtaType === PaginationCTAType.Previous) {
			clickedPageNumber = currentPageNumber - 1;
		}

		if (paginationCtaType === PaginationCTAType.Next) {
			clickedPageNumber = currentPageNumber + 1;
		}

		onItemClick({
			paginationCtaType,
			currentPageNumber,
			clickedPageNumber,
		});
	};

	return (
		<div className="sl-pagination">
			<div className="sl-pagination__wrapper">
				<Button
					size="s"
					buttonType="secondary"
					disabled={currentPageNumber === 1}
					onClick={() => handleItemClick(PaginationCTAType.Previous)}
					asChild
				>
					<LocalizedLink to={generateUrl(currentPageNumber - 1)}>
						{i18n.t('web-navigation.pagination-previous')}
					</LocalizedLink>
				</Button>
				{pages.map((page) => (
					<Button
						key={page.key}
						size="s"
						disabled={page.isDisabled}
						buttonType={getButtonType(
							currentPageNumber,
							+page.pageNumber,
							page.isDisabled,
						)}
						{...(!page.isDisabled && {
							onClick: () => {
								handleItemClick(
									PaginationCTAType.PageNumber,
									+page.pageNumber,
								);
							},
						})}
						asChild
					>
						<LocalizedLink to={generateUrl(+page.pageNumber)}>
							{page.pageNumber}
						</LocalizedLink>
					</Button>
				))}
				<Button
					size="s"
					buttonType="secondary"
					disabled={currentPageNumber === pagesCount}
					onClick={() => handleItemClick(PaginationCTAType.Next)}
					asChild
				>
					<LocalizedLink to={generateUrl(currentPageNumber + 1)}>
						{i18n.t('web-navigation.pagination-next')}
					</LocalizedLink>
				</Button>
			</div>
		</div>
	);
}
